import lodash from "lodash";

import { getCallable } from "../_sdk/client";
import { apiName, endpoints } from "./endpoints";
import {
    ConnectEmbedLinkDomainsRequest, ConnectEmbedLinkDomainsResponse,
    CopyPresentationRequest, CopyPresentationResponse,
    CreateLinkRequest, CreateLinkResponse,
    CreatePresentationWithSlidesAndResourceRequest, CreatePresentationWithSlidesAndResourceResponse,
    DeleteLinkRequest, DeleteLinkResponse,
    DeletePresentationRequest, DeletePresentationResponse,
    GetLinkRequest, GetLinkResponse,
    GetPresentationLastModifiedDataRequest, GetPresentationLastModifiedDataResponse,
    GetPresentationRequest, GetPresentationResponse,
    GetPresentationsMetadataRequest, GetPresentationsMetadataResponse,
    GetSlideCountRequest, GetSlideCountResponse,
    GetSlidesMetadataRequest, GetSlidesMetadataResponse,
    IncrementLinkViewCountRequest, IncrementLinkViewCountResponse,
    MoveWorkspaceRequest, MoveWorkspaceResponse,
    RecordActivityRequest, RecordActivityResponse,
    UpdateLinkPasswordRequest, UpdateLinkPasswordResponse,
    UpdateLinkRequest, UpdateLinkResponse,
    GeneratePresentationRequest,
    UpdatePresentationRequest, UpdatePresentationResponse,
    UpdateSlidesMetadataRequest, UpdateSlidesMetadataResponse, GeneratePresentationResponse
} from "./types";

const getPresentation = getCallable<GetPresentationRequest, GetPresentationResponse>(apiName, "getPresentation", {
    ...endpoints.getPresentation,
    composer: request => {
        return {
            url: `/presentations/${encodeURIComponent(request.id)}`,
            options: {
                method: "GET"
            }
        };
    }
});

const createPresentationWithSlidesAndResource = getCallable<CreatePresentationWithSlidesAndResourceRequest, CreatePresentationWithSlidesAndResourceResponse>(apiName, "createPresentationWithSlidesAndResource", {
    ...endpoints.createPresentationWithSlidesAndResource,
    composer: request => {
        return {
            url: "/presentations",
            options: {
                method: "POST",
                body: JSON.stringify(request)
            }
        };
    }
});

const updatePresentation = getCallable<UpdatePresentationRequest, UpdatePresentationResponse>(apiName, "updatePresentation", {
    ...endpoints.updatePresentation,
    composer: request => {
        return {
            url: `/presentations/${encodeURIComponent(request.id)}`,
            options: {
                method: "PUT",
                body: JSON.stringify(request)
            }
        };
    }
});

const deletePresentation = getCallable<DeletePresentationRequest, DeletePresentationResponse>(apiName, "deletePresentation", {
    ...endpoints.deletePresentation,
    composer: request => {
        return {
            url: `/presentations/${encodeURIComponent(request.id)}`,
            options: {
                method: "DELETE"
            }
        };
    }
});

const copyPresentation = getCallable<CopyPresentationRequest, CopyPresentationResponse>(apiName, "copyPresentation", {
    ...endpoints.copyPresentation,
    composer: request => {
        return {
            url: `/presentations/${encodeURIComponent(request.id)}/copy`,
            options: {
                method: "PUT",
                body: JSON.stringify(request)
            }
        };
    }
});

const moveWorkspace = getCallable<MoveWorkspaceRequest, MoveWorkspaceResponse>(apiName, "moveWorkspace", {
    ...endpoints.moveWorkspace,
    composer: request => {
        return {
            url: `/presentations/${encodeURIComponent(request.id)}/move`,
            options: {
                method: "PUT",
                body: JSON.stringify(request)
            }
        };
    }
});

const recordActivity = getCallable<RecordActivityRequest, RecordActivityResponse>(apiName, "recordActivity", {
    ...endpoints.recordActivity,
    composer: request => {
        return {
            url: `/presentations/${encodeURIComponent(request.id)}/activity`,
            options: {
                method: "PUT",
                body: JSON.stringify(request)
            }
        };
    }
});

const getSlidesMetadata = getCallable<GetSlidesMetadataRequest, GetSlidesMetadataResponse>(apiName, "getSlidesMetadata", {
    ...endpoints.getMetadata,
    composer: request => {
        return {
            url: `/presentations/${encodeURIComponent(request.id)}/slidesMetadata`,
            options: {
                method: "GET"
            }
        };
    }
});

const updateSlidesMetadata = getCallable<UpdateSlidesMetadataRequest, UpdateSlidesMetadataResponse>(apiName, "updateSlidesMetadata", {
    ...endpoints.updateMetadata,
    composer: request => {
        return {
            url: `/presentations/${encodeURIComponent(request.id)}/slidesMetadata`,
            options: {
                method: "PUT",
                body: JSON.stringify(request)
            }
        };
    }
});

const createLink = getCallable<CreateLinkRequest, CreateLinkResponse>(apiName, "createLink", {
    ...endpoints.createLink,
    composer: request => {
        return {
            url: `/presentations/${encodeURIComponent(request.id)}/links`,
            options: {
                method: "POST",
                body: JSON.stringify(request)
            }
        };
    }
});

const getLink = getCallable<GetLinkRequest, GetLinkResponse>(apiName, "getLink", {
    ...endpoints.getLink,
    composer: request => {
        return {
            url: `/presentations/${encodeURIComponent(request.id)}/links/${encodeURIComponent(request.linkId)}`,
            options: {
                method: "GET"
            }
        };
    }
});

const updateLink = getCallable<UpdateLinkRequest, UpdateLinkResponse>(apiName, "updateLink", {
    ...endpoints.updateLink,
    composer: request => {
        return {
            url: `/presentations/${encodeURIComponent(request.id)}/links/${encodeURIComponent(request.linkId)}`,
            options: {
                method: "PUT",
                body: JSON.stringify(request)
            }
        };
    }
});

const deleteLink = getCallable<DeleteLinkRequest, DeleteLinkResponse>(apiName, "deleteLink", {
    ...endpoints.deleteLink,
    composer: request => {
        return {
            url: `/presentations/${encodeURIComponent(request.id)}/links/${encodeURIComponent(request.linkId)}`,
            options: {
                method: "DELETE"
            }
        };
    }
});

const updateLinkPassword = getCallable<UpdateLinkPasswordRequest, UpdateLinkPasswordResponse>(apiName, "updateLinkPassword", {
    ...endpoints.updateLinkPassword,
    composer: request => {
        return {
            url: `/presentations/${encodeURIComponent(request.id)}/links/${encodeURIComponent(request.linkId)}/password`,
            options: {
                method: "PUT",
                body: JSON.stringify(request)
            }
        };
    }
});

const connectEmbedLinkDomains = getCallable<ConnectEmbedLinkDomainsRequest, ConnectEmbedLinkDomainsResponse>(apiName, "connectEmbedLinkDomains", {
    ...endpoints.connectEmbedLinkDomains,
    composer: request => {
        return {
            url: `/presentations/${encodeURIComponent(request.id)}/links/${encodeURIComponent(request.linkId)}/embedLinkDomains`,
            options: {
                method: "PUT",
                body: JSON.stringify(request)
            }
        };
    }
});

const incrementLinkViewCount = getCallable<IncrementLinkViewCountRequest, IncrementLinkViewCountResponse>(apiName, "incrementLinkViewCount", {
    ...endpoints.incrementLinkViewCount,
    composer: request => {
        return {
            url: `/presentations/${encodeURIComponent(request.id)}/links/${encodeURIComponent(request.linkId)}/viewCount`,
            options: {
                method: "PUT",
                body: JSON.stringify(request)
            }
        };
    }
});

const getPresentationsMetadata = getCallable<GetPresentationsMetadataRequest, GetPresentationsMetadataResponse>(apiName, "getPresentationsMetadata", {
    ...endpoints.getPresentationsMetadata,
    composer: request => {
        const chunks = lodash.chunk(request.presentationIds, 500);
        return chunks.map(presentationIds => ({
            url: "/presentations/metadata",
            options: {
                method: "POST",
                body: JSON.stringify({ presentationIds, workspaceId: request.workspaceId })
            }
        }));
    },
    collector: responses => {
        return responses.flat();
    }
});

const getSlideCount = getCallable<GetSlideCountRequest, GetSlideCountResponse>(apiName, "getSlideCount", {
    ...endpoints.getSlideCount,
    composer: request => {
        return {
            url: `/presentations/slideCount`,
            options: {
                method: "POST",
                body: JSON.stringify(request)
            }
        };
    }
});

const getPresentationLastModifiedData = getCallable<GetPresentationLastModifiedDataRequest, GetPresentationLastModifiedDataResponse>(apiName, "getPresentationLastModifiedData", {
    ...endpoints.getPresentationLastModifiedData,
    composer: request => {
        return {
            url: `/presentations/${encodeURIComponent(request.id)}/lastModifiedData`,
            options: {
                method: "GET"
            }
        };
    }
});

export const callables = {
    getPresentation,
    createPresentationWithSlidesAndResource,
    updatePresentation,
    deletePresentation,

    copyPresentation,
    moveWorkspace,
    recordActivity,

    getSlidesMetadata,
    updateSlidesMetadata,

    createLink,
    getLink,
    updateLink,
    deleteLink,
    updateLinkPassword,
    connectEmbedLinkDomains,
    incrementLinkViewCount,

    getPresentationsMetadata,

    getSlideCount,
    getPresentationLastModifiedData,
};
